import { bmsFaults, pcuWarningsFaults } from '../lib/fault.support';
import { CustomInverterType, DataType, ErrorFeature, InverterData, InverterDataComposer, ReadWrite, RegisterTable } from '../lib/inverter.model';
import { V3BmsFaultConfig, V3BmsRealTimeConfig, createPackFaultCellVoltage, createPackRTCellVoltage } from './inverter.config';

export const V3PcuDetails: Partial<RegisterTable> = {
  AddressMask_PCU1: {
    address: 0x6000,
    name: 'AddressMask_PCU1',
    dataType: DataType.U64,
    description: 'AddressMask_PCU1',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  PCU_Number: {
    address: 0x6004,
    name: 'PCU_Number',
    dataType: DataType.U16,
    description: 'PCU_Number',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_ID: {
    address: 0x6010,
    name: 'PCU_ID',
    dataType: DataType.U16,
    description: 'PCU_ID',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'ID',
    },
  },
  PCU_SN: {
    address: 0x6011,
    name: 'PCU_SN',
    dataType: DataType.ASCII,
    description: 'PCU_SN',
    access: ReadWrite.Read,
    quantity: 10,
    custom: {
      translate: 'PCU SN',
    },
  },
  PCU_Software_Version: {
    address: 0x601B,
    name: 'PCU_Software_Version',
    dataType: DataType.U32,
    description: 'PCU_Software_Version',
    access: ReadWrite.Read,
    quantity: 2,
    custom: {
      translate: 'PCU Software Version',
    },
  },
  PCU_Hardware_Version: {
    address: 0x601D,
    name: 'PCU_Hardware_Version',
    dataType: DataType.U16,
    description: 'PCU_Hardware_Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Can1_Version: {
    address: 0x601E,
    name: 'PCU_Can1_Version',
    dataType: DataType.U16,
    description: 'PCU_Can1_Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Can2_Version: {
    address: 0x601F,
    name: 'PCU_Can2_Version',
    dataType: DataType.U16,
    description: 'PCU_Can2_Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Work_State: {
    address: 0x6020,
    name: 'PCU_Work_State',
    dataType: DataType.U16,
    description: `Module Status
    0：Wait to upgrade
    1: Check
    2：Normal
    3: Battery Activated
    4: Fault
    5：Permanent Fault`,
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'PCU Work State',
    },
  },
  PCU_Internal_Temperature: {
    address: 0x6021,
    name: 'PCU_Internal_Temperature',
    dataType: DataType.U16,
    description: 'Chassis internal temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    custom: {
      translate: 'Internal Temperature',
    },
  },
  PCU_Radiator_Temperature1: {
    address: 0x6022,
    name: 'PCU_Radiator_Temperature1',
    dataType: DataType.I16,
    description: 'Heatsink 1 temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    custom: {
      translate: 'Radiator Temperature 1',
    },
  },
  PCU_Radiator_Temperature2: {
    address: 0x6023,
    name: 'PCU_Radiator_Temperature2',
    dataType: DataType.I16,
    description: 'Heatsink 2 temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    custom: {
      translate: 'Radiator Temperature 2',
    },
  },
  PCU_Warning1: {
    address: 0x6024,
    name: 'PCU_Warning1',
    dataType: DataType.U16,
    description: 'Warning 1',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Warning2: {
    address: 0x6025,
    name: 'PCU_Warning2',
    dataType: DataType.U16,
    description: 'Warning 2',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Warning3: {
    address: 0x6026,
    name: 'PCU_Warning3',
    dataType: DataType.U16,
    description: 'Warning 3',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Warning4: {
    address: 0x6027,
    name: 'PCU_Warning4',
    dataType: DataType.U16,
    description: 'Warning 4',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Fault1: {
    address: 0x6028,
    name: 'PCU_Fault1',
    dataType: DataType.U16,
    description: 'Fault 1',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Fault2: {
    address: 0x6029,
    name: 'PCU_Fault2',
    dataType: DataType.U16,
    description: 'Fault 2',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Fault3: {
    address: 0x602A,
    name: 'PCU_Fault3',
    dataType: DataType.U16,
    description: 'Fault 3',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_Fault4: {
    address: 0x602B,
    name: 'PCU_Fault4',
    dataType: DataType.U16,
    description: 'Fault 4',
    access: ReadWrite.Read,
    quantity: 1,
  },
  PCU_DC_High_Voltage: {
    address: 0x602C,
    name: 'PCU_DC_High_Voltage',
    dataType: DataType.U16,
    description: 'DC high voltage side voltage',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'DC High Voltage',
    },
  },
  PCU_DC_Low_Voltage: {
    address: 0x602D,
    name: 'PCU_DC_Low_Voltage',
    dataType: DataType.U16,
    description: 'DC low voltage side voltage',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'DC Low Voltage',
    },
  },
  PCU_Low_Current: {
    address: 0x602E,
    name: 'PCU_Low_Current',
    dataType: DataType.I16,
    description: 'DC low voltage side current',
    unit: 'A',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Low Current',
    },
  },
  PCU_Low_Power: {
    address: 0x602F,
    name: 'PCU_Low_Power',
    dataType: DataType.I16,
    description: 'DC low voltage side power',
    unit: 'kW',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.01,
    custom: {
      translate: 'Low Power',
    },
  },
  PCU_BusP: {
    address: 0x6030,
    name: 'PCU_BusP',
    dataType: DataType.U16,
    description: 'Positive bus voltage',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_BusN: {
    address: 0x6031,
    name: 'PCU_BusN',
    dataType: DataType.U16,
    description: 'Negative bus voltage',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_FC_Voltage1: {
    address: 0x6032,
    name: 'PCU_FC_Voltage1',
    dataType: DataType.U16,
    description: 'Fly Across Capacitor Voltage 1',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_FC_Voltage2: {
    address: 0x6033,
    name: 'PCU_FC_Voltage2',
    dataType: DataType.U16,
    description: 'Fly across capacitor voltage 2',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_FC_Voltage3: {
    address: 0x6034,
    name: 'PCU_FC_Voltage3',
    dataType: DataType.U16,
    description: 'Fly across capacitor voltage 3',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_FC_Voltage4: {
    address: 0x6035,
    name: 'PCU_FC_Voltage4',
    dataType: DataType.U16,
    description: 'Fly across capacitor voltage 4',
    unit: 'V',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_IL_Current1: {
    address: 0x6036,
    name: 'PCU_IL_Current1',
    dataType: DataType.I16,
    description: 'Inductive current branch 1',
    unit: 'A',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_IL_Current2: {
    address: 0x6037,
    name: 'PCU_IL_Current2',
    dataType: DataType.I16,
    description: 'Inductive current branch 2',
    unit: 'A',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_IL_Current3: {
    address: 0x6038,
    name: 'PCU_IL_Current3',
    dataType: DataType.I16,
    description: 'Inductive current branch 3',
    unit: 'A',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
  PCU_IL_Current4: {
    address: 0x6039,
    name: 'PCU_IL_Current4',
    dataType: DataType.I16,
    description: 'Inductive current branch 4',
    unit: 'A',
    access: ReadWrite.Read,
    quantity: 1,
    accuracy: 0.1,
  },
};

export const PCU_WORK_STATE_MAP: Record<number, string> = {
  0: 'Wait To Upgrade',
  1: 'Check',
  2: 'Normal',
  3: 'Battery Activated',
  4: 'Fault',
  5: 'Permanent Fault',
};

export const ComposeV3PcuDetails: Partial<InverterDataComposer> = {
  PCU_Work_State: (data: Partial<InverterData>) => {
    const value = data.PCU_Work_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    const stateText = PCU_WORK_STATE_MAP[value] || 'Unknown';
    data.PCU_Work_State = stateText;
    return data;
  },
  PCU_Software_Version: (data: Partial<InverterData>) => {
    const value = data.PCU_Software_Version;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    data.PCU_Software_Version = value.toString(16)
      .split(/(?=(?:..)*$)/)
      .map((v, index) => {
        if (index === 0) {
          return String.fromCodePoint(parseInt(v, 16));
        }
        return `${parseInt(v, 16)}`.padStart(2, '0');
      }).join('');
    return data;
  },
  PCU_events: (data: Partial<InverterData>) => {
    const eventKeys = [
      'PCU_Warning1',
      'PCU_Warning2',
      'PCU_Warning3',
      'PCU_Warning4',
      'PCU_Fault1',
      'PCU_Fault2',
      'PCU_Fault3',
      'PCU_Fault4',
    ];

    const events: ErrorFeature[] = [];

    eventKeys.map((key) => {
      const address = V3PcuDetails[key as keyof typeof V3PcuDetails]?.address;
      const value = data[key] as number;
      const errorFeatures = pcuWarningsFaults[address as number];

      for (let i = 0; i < 16; i++) {
        const bit = 1 << i;
        if ((value & bit) !== 0) {
          if (errorFeatures[bit]) {
            events.push(errorFeatures[bit]);
          } else {
            events.push({
              description: `Unknown error ${bit}/0x${address?.toString(16)}`,
              source: 'Unknown',
              classification: 'Unknown',
            });
          }
        }
      }
    });

    data.PCU_events = {
      type: CustomInverterType.EventList,
      value: {
        descriptions: events,
        date: new Date(),
        id: 0,
        index: 0,
      },
    };

    return data;
  },
};

export const V3PcuConfig: Partial<RegisterTable> = {
  AddressMask_PCU2: {
    address: 0x6040,
    name: 'AddressMask_PCU2',
    dataType: DataType.U64,
    description: 'AddressMask_PCU2',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  PCU_Inquire: {
    address: 0x6044,
    name: 'PCU_Inquire',
    dataType: DataType.U16,
    description: 'PCU Query Control Word. Select the PCU module to be read or set by setting this register.',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  PCU_Remote_On_Off: {
    address: 0x6045,
    name: 'PCU_Remote_On_Off',
    dataType: DataType.U16,
    description: `DC Module Remote On/Off'
    0: Shutdown
    1: Disable charging
    2: Discharge prohibited
    3: Allow charging and discharging`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  PCU_Max_Charge_Current: {
    address: 0x6046,
    name: 'PCU_Max_Charge_Current',
    dataType: DataType.U16,
    description: 'Max. charging current of DC module',
    unit: 'A',
    access: ReadWrite.ReadWrite,
    accuracy: 0.1,
    quantity: 1,
  },
  PCU_Max_Discharge_Current: {
    address: 0x6047,
    name: 'PCU_Max_Discharge_Current',
    dataType: DataType.U16,
    description: 'Max. discharge current of DC module',
    unit: 'A',
    access: ReadWrite.ReadWrite,
    accuracy: 0.1,
    quantity: 1,
  },
};

export const V3BduDetails: Partial<RegisterTable> = {
  AddressMask_BDU1: {
    address: 0x6080,
    name: 'AddressMask_BDU1',
    dataType: DataType.U64,
    description: 'AddressMask_BDU1',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  BDU_Number: {
    address: 0x6084,
    name: 'BDU_Number',
    dataType: DataType.U16,
    description: 'BDU_Number',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BDU_ID: {
    address: 0x6090,
    name: 'BDU_ID',
    dataType: DataType.U16,
    description: 'BDU_ID',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'BDU ID',
    },
  },
  BDU_SN: {
    address: 0x6091,
    name: 'BDU_SN',
    dataType: DataType.ASCII,
    description: 'BDU_SN',
    access: ReadWrite.Read,
    quantity: 10,
    custom: {
      translate: 'BDU SN',
    },
  },
  FW_VERSION: {
    address: 0x609B,
    name: 'FW_VERSION',
    dataType: DataType.ASCII,
    description: 'FW_VERSION',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'FW Version',
    },
  },
  Software_Version_Stage_BDU: {
    address: 0x609B,
    name: 'Software_Version_Stage_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Stage_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Major_BDU: {
    address: 0x609C,
    name: 'Software_Version_Major_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Major_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Custom_BDU: {
    address: 0x609D,
    name: 'Software_Version_Custom_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Custom_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Minor_BDU: {
    address: 0x609E,
    name: 'Software_Version_Minor_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Minor_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BDU_Fault1: {
    address: 0x609F,
    name: 'BDU_Fault1',
    dataType: DataType.U16,
    description: 'BDU_Fault1',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'BDU Fault',
    },
  },
  BDU_Battery_Number: {
    address: 0x60A0,
    name: 'BDU_Battery_Number',
    dataType: DataType.U16,
    description: 'BDU_Battery_Number',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const ComposeV3BduDetails: Partial<InverterDataComposer> = {
  FW_VERSION: (data: Partial<InverterData>) => {
    if (
        data.Software_Version_Stage_BDU &&
        data.Software_Version_Major_BDU &&
        data.Software_Version_Custom_BDU &&
        data.Software_Version_Minor_BDU
      ) {
        data.FW_VERSION = `${(data.Software_Version_Stage_BDU as string)?.trim()}${(data.Software_Version_Major_BDU as string)?.trim()}${
          (data.Software_Version_Custom_BDU as string)?.trim()}${(data.Software_Version_Minor_BDU as string)?.trim()}`;
    }
    return data;
  },
  BDU_Fault1: (data: Partial<InverterData>) => {
    const value = data.BDU_Fault1;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    let activeCount = 0;
    for (let i = 0; i < 16; i++) {
      if ((value & (1 << i)) !== 0) {
        activeCount++;
      }
    }
    data.BDU_Fault1 = activeCount === 0 ? 'OK' : `${activeCount}`;
    return data;
  },
};

export const V3BduConfig: Partial<RegisterTable> = {
  AddressMask_BDU2: {
    address: 0x60C0,
    name: 'AddressMask_BDU2',
    dataType: DataType.U64,
    description: 'AddressMask_BDU2',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  BDU_Inquire: {
    address: 0x60C4,
    name: 'BDU_Inquire',
    dataType: DataType.U16,
    description: 'BDU Query Control Word. Select the BDU module to be read or set by setting this register.',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V3BmsDetails: Partial<RegisterTable> = {
  AddressMask_BMS1_System: {
    address: 0x9000,
    name: 'AddressMask_BMS1_System',
    dataType: DataType.U64,
    description: 'AddressMask_BMS1_System',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  BMS_Sys_Time: {
    address: 0x9004,
    name: 'BMS_Sys_Time',
    dataType: DataType.U32,
    description: 'BMS system clock',
    access: ReadWrite.Read,
    quantity: 2,
    custom: {
      translate: 'BMS System Time',
    },
  },
  BMS_Converted_Sys_Time: {
    address: 0x9004,
    name: 'BMS_Sys_Time',
    dataType: DataType.U32,
    description: 'BMS system clock',
    access: ReadWrite.Read,
    quantity: 2,
    custom: {
      translate: 'BMS System Time',
    },
  },
  BMS_CAN_Version: {
    address: 0x9006,
    name: 'BMS_CAN_Version',
    dataType: DataType.U16,
    description: 'CAN protocol version number',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BMS_Manufacture_Name: {
    address: 0x9007,
    name: 'BMS_Manufacture_Name0',
    dataType: DataType.U16,
    description: 'Manufacturer Information 0',
    access: ReadWrite.Read,
    quantity: 4,
  },
  BMS_Version: {
    address: 0x900B,
    name: 'BMS_Version',
    dataType: DataType.U16,
    description: 'BMS version number',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'BMS Version',
    },
  },
  Cell_Type: {
    address: 0x900C,
    name: 'Cell_Type',
    dataType: DataType.U16,
    description: 'Cell type',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BaPack_Number: {
    address: 0x900D,
    name: 'BaPack_Number',
    dataType: DataType.U16,
    description: 'High 8 bits: the number of battery packs in parallel; Lower 8 bits: the number of battery strings in the battery pack',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Realtime_Capacity: {
    address: 0x900E,
    name: 'Realtime_Capacity',
    dataType: DataType.U16,
    description: 'Real-time remaining capacity',
    access: ReadWrite.Read,
    unit: '%',
    quantity: 1,
  },
  Total_Voltage: {
    address: 0x900F,
    name: 'Total_Voltage',
    dataType: DataType.U16,
    description: 'Total voltage',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Total Voltage',
    },
  },
  Total_Current: {
    address: 0x9010,
    name: 'Total_Current',
    dataType: DataType.U16,
    description: 'Total current',
    access: ReadWrite.Read,
    unit: 'A',
    quantity: 1,
    accuracy: 0.1,
  },
  Cell_Average_Temperature: {
    address: 0x9011,
    name: 'Cell_Average_Temperature',
    dataType: DataType.U16,
    description: 'Average cell temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
  },
  SOC: {
    address: 0x9012,
    name: 'SOC',
    dataType: DataType.U16,
    description: 'State of charge',
    access: ReadWrite.Read,
    unit: '%',
    quantity: 1,
    custom: {
      translate: 'SOC',
    },
  },
  SOH: {
    address: 0x9013,
    name: 'SOH',
    dataType: DataType.U16,
    description: 'State of health',
    access: ReadWrite.Read,
    unit: '%',
    quantity: 1,
    custom: {
      translate: 'SOH',
    },
  },
  BMS_Sys_Protect0: {
    address: 0x9014,
    name: 'BMS_Sys_Protect0',
    dataType: DataType.U16,
    description: 'BMS system protection information0',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BMS_Sys_Protect1: {
    address: 0x9015,
    name: 'BMS_Sys_Protect1',
    dataType: DataType.U16,
    description: 'BMS system protection information 1',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BMS_Sys_Alarm0: {
    address: 0x9016,
    name: 'BMS_Sys_Alarm0',
    dataType: DataType.U16,
    description: 'BMS system alarm information 0',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BMS_Sys_Alarm1: {
    address: 0x9017,
    name: 'BMS_Sys_Alarm1',
    dataType: DataType.U16,
    description: 'BMS system alarm information 1',
    access: ReadWrite.Read,
    quantity: 1,
  },
  /**
   * TODO: if it is read together with the other registers, the modbus response is 9002. Why ???
   */
  BMS_Inquire: {
    address: 0x9020,
    name: 'BMS_Inquire',
    dataType: DataType.U16,
    description: `Battery query control word
      Bit0-7: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
      Bit8-11: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
      Bit12-15: The serial number of the fault to be queried, the valid range is 0~5, and 0 means the most recent fault`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  BMS_parallel_ID: {
    address: 0x9020,
    name: 'BMS_Inquire',
    dataType: DataType.U16,
    description: `Battery query control word
      Bit0-7: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
      Bit8-11: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
      Bit12-15: The serial number of the fault to be queried, the valid range is 0~5, and 0 means the most recent fault`,
    access: ReadWrite.ReadWrite,
    quantity: 1,
    custom: {
      translate: 'ID',
    },
  },
};

export const ComposeV3BmsDetails: Partial<InverterDataComposer> = {
  BMS_parallel_ID: (data: Partial<InverterData>) => {
    const value = data.BMS_Inquire as number;
    /**
     *  parallel: 0x0F00
     */
    const parallel = (value >> 8) & 0x0F;
    return { ...data, BMS_parallel_ID: parallel };
  },
  BMS_Version: (data: Partial<InverterData>) => {
    const value = data.BMS_Version;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    data.BMS_Version = `V${value.toString().padStart(6, '0')}`;
    return data;
  },
  BMS_Converted_Sys_Time: (data: Partial<InverterData>) => {
    const binary = data.BMS_Sys_Time?.toString(2).padStart(32, '0').split('').reverse().join('');
    if (!binary) {
      data.BMS_Converted_Sys_Time = null;
      return data;
    }

    const second = parseInt(binary.slice(0, 6).split('').reverse().join(''), 2);
    const min = parseInt(binary.slice(6, 12).split('').reverse().join(''), 2);
    const hour = parseInt(binary.slice(12, 17).split('').reverse().join(''), 2);
    const day = parseInt(binary.slice(17, 22).split('').reverse().join(''), 2);
    const month = parseInt(binary.slice(22, 26).split('').reverse().join(''), 2);
    // year start from 2000 to 2063
    const year = 2000 + parseInt(binary.slice(26, 32).split('').reverse().join(''), 2);

    data.BMS_Converted_Sys_Time = `${year}-${month}-${day} ${hour}:${min}:${second}`;
    return data;
  },
  BMS_events: (data: Partial<InverterData>) => {
    const eventKeys = [
      'BMS_Sys_Protect0',
      'BMS_Sys_Protect1',
      'BMS_Sys_Alarm0',
      'BMS_Sys_Alarm1',
    ];

    const events: ErrorFeature[] = [];

    eventKeys.map((key) => {
      const address = V3BmsDetails[key as keyof typeof V3BmsDetails]?.address;
      const value = data[key] as number;
      const errorFeatures = bmsFaults[address as number];

      for (let i = 0; i < 16; i++) {
        const bit = 1 << i;
        if ((value & bit) !== 0) {
          if (errorFeatures[bit]) {
            events.push(errorFeatures[bit]);
          } else {
            events.push({
              description: `Unknown error ${bit}/0x${address?.toString(16)}`,
              source: 'Unknown',
              classification: 'Unknown',
            });
          }
        }
      }
    });

    data.BMS_events = {
      type: CustomInverterType.EventList,
      value: {
        descriptions: events,
        date: new Date(),
        id: 0,
        index: 0,
      },
    };
    return data;
  },
};

export const V3BmsRealtime: Partial<RegisterTable> = {
  AddressMask_BMS2_Realtime: {
    address: 0x9040,
    name: 'AddressMask_BMS2_Realtime',
    dataType: DataType.U64,
    description: 'AddressMask_BMS2_Realtime',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  Pack_RT_ID: {
    address: 0x9044,
    name: 'Pack_RT_ID',
    dataType: DataType.U16,
    description: 'Real-time data battery pack number. High 8 bits: battery pack serial number Lower 8 bits: battery pack serial number',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Pack_RT_Timestamp: {
    address: 0x9045,
    name: 'Pack_RT_Timestamp',
    dataType: DataType.U32,
    description: 'Real-time data timestamp',
    access: ReadWrite.Read,
    quantity: 2,
  },
  Pack_RT_SN: {
    address: 0x9047,
    name: 'Pack_RT_SN',
    dataType: DataType.ASCII,
    description: 'Device serial number',
    access: ReadWrite.Read,
    quantity: 10,
    custom: {
      translate: 'Pack SN',
    },
  },
  Pack_RT_Cell_Max_Voltage: {
    address: 0x9069,
    name: 'Pack_RT_Cell_Max_Voltage',
    dataType: DataType.U16,
    description: 'The highest voltage of the battery',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.001,
    custom: {
      translate: 'Cell Max Voltage',
    },
  },
  Pack_RT_Cell_Min_Voltage: {
    address: 0x906A,
    name: 'Pack_RT_Cell_Min_Voltage',
    dataType: DataType.U16,
    description: 'Minimum battery voltage',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.001,
    custom: {
      translate: 'Cell Min Voltage',
    },
  },
  Pack_RT_Temperature1_Pack: {
    address: 0x906B,
    name: 'Pack_RT_Temperature1_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 1',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 1',
    },
  },
  Pack_RT_Temperature2_Pack: {
    address: 0x906C,
    name: 'Pack_RT_Temperature2_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 2',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 2',
    },
  },
  Pack_RT_Temperature3_Pack: {
    address: 0x906D,
    name: 'Pack_RT_Temperature3_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 3',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 3',
    },
  },
  Pack_RT_Temperature4_Pack: {
    address: 0x906E,
    name: 'Pack_RT_Temperature4_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 4',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 4',
    },
  },
  Pack_RT_Temperature_MOS: {
    address: 0x906F,
    name: 'Pack_RT_Temperature_MOS',
    dataType: DataType.I16,
    description: 'MOS tube temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
  },
  Pack_RT_Temperature_Env: {
    address: 0x9070,
    name: 'Pack_RT_Temperature_Env',
    dataType: DataType.I16,
    description: 'Ambient temperature in the battery pack',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
  },
  Pack_RT_Current: {
    address: 0x9071,
    name: 'Pack_RT_Current',
    dataType: DataType.I16,
    description: 'Battery pack current',
    access: ReadWrite.Read,
    unit: 'A',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Current',
    },
  },
  Pack_RT_Remaining_Capacity: {
    address: 0x9072,
    name: 'Pack_RT_Remaining_Capacity',
    dataType: DataType.U16,
    description: 'Remaining capacity',
    access: ReadWrite.Read,
    unit: 'Ah',
    quantity: 1,
    accuracy: 0.1,
  },
  Pack_RT_FullCharge_Capacity: {
    address: 0x9073,
    name: 'Pack_RT_FullCharge_Capacity',
    dataType: DataType.U16,
    description: 'Full charge capacity',
    access: ReadWrite.Read,
    unit: 'Ah',
    quantity: 1,
    accuracy: 0.1,
  },
  Pack_RT_Cycles: {
    address: 0x9074,
    name: 'Pack_RT_Cycles',
    dataType: DataType.U16,
    description: 'Cycles',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Cycles',
    },
  },
  Pack_RT_Balance_State: {
    address: 0x9075,
    name: 'Pack_RT_Balance_State',
    dataType: DataType.U16,
    description: 'Equilibrium',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Balance State',
    },
  },
  Pack_RT_Alarm_State: {
    address: 0x9076,
    name: 'Pack_RT_Alarm_State',
    dataType: DataType.U16,
    description: 'Alarm status',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Alarm State',
    },
  },
  Pack_RT_Protect_State: {
    address: 0x9077,
    name: 'Pack_RT_Protect_State',
    dataType: DataType.U16,
    description: 'Protection status',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Protection State',
    },
  },
  Pack_RT_Fault_State: {
    address: 0x9078,
    name: 'Pack_RT_Fault_State',
    dataType: DataType.U16,
    description: 'Fault state',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Fault State',
    },
  },
  Pack_RT_Total_Voltage: {
    address: 0x9079,
    name: 'Pack_RT_Total_Voltage',
    dataType: DataType.U16,
    description: 'Total Voltage',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Total Voltage',
    },
  },
  Pack_RT_SOC: {
    address: 0x907A,
    name: 'Pack_RT_SOC',
    dataType: DataType.U16,
    description: 'State of charge',
    access: ReadWrite.Read,
    unit: '%',
    quantity: 1,
    custom: {
      translate: 'SOC',
    },
  },
};

const Pack_RT_Cell_Voltage_Config_Start = V3BmsRealTimeConfig.Pack_RT_Cell_Voltage.start;
for (let i = 1; i <= V3BmsRealTimeConfig.Pack_RT_Cell_Voltage.length; i++) {
  const { pack_rt_cell_voltage } = createPackRTCellVoltage(
    i,
    Pack_RT_Cell_Voltage_Config_Start + i,
  );
  V3BmsRealtime[`Pack_RT_Cell_Voltage${i}`] = pack_rt_cell_voltage;
}

/** Helper to count active bits in a 16-bit value */
const countActiveBits = (value: number): number => {
  let count = 0;
  for (let i = 0; i < 16; i++) {
    if ((value & (1 << i)) !== 0) {
      count++;
    }
  }
  return count;
};

/** Helper to get concatenated descriptions for active bits from bmsFaults mapping */
const getActiveErrorDescriptions = (value: number, registerAddress: number): string => {
  const registerFaults = bmsFaults[registerAddress];
  if (!registerFaults) {
    return `${countActiveBits(value)}`;
  }

  const activeDescriptions: string[] = [];
  for (let i = 0; i < 16; i++) {
    const bitMask = 1 << i;
    if ((value & bitMask) !== 0) {
      const fault = registerFaults[bitMask];
      if (fault?.description) {
        activeDescriptions.push(fault.description);
      }
    }
  }

  return activeDescriptions.length > 0 ? activeDescriptions.join(', ') : `${countActiveBits(value)}`;
};

export const ComposeV3BmsRealtime: Partial<InverterDataComposer> = {
  Pack_RT_Balance_State: (data: Partial<InverterData>) => {
    const value = data.Pack_RT_Balance_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    const activeCount = countActiveBits(value);
    data.Pack_RT_Balance_State = activeCount === 0 ? 'OK' : `${activeCount}`;
    return data;
  },
  Pack_RT_Alarm_State: (data: Partial<InverterData>) => {
    const value = data.Pack_RT_Alarm_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    data.Pack_RT_Alarm_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9076);
    return data;
  },
  Pack_RT_Protect_State: (data: Partial<InverterData>) => {
    const value = data.Pack_RT_Protect_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    data.Pack_RT_Protect_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9077);
    return data;
  },
  Pack_RT_Fault_State: (data: Partial<InverterData>) => {
    const value = data.Pack_RT_Fault_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    data.Pack_RT_Fault_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9078);
    return data;
  },
};

export const V3BmsFaults: Partial<RegisterTable> = {
  AddressMask_BMS3_Fault: {
    address: 0x9080,
    name: 'AddressMask_BMS3_Fault',
    dataType: DataType.U64,
    description: 'AddressMask_BMS3_Fault',
    access: ReadWrite.Read,
    quantity: 4,
    isMask: true,
  },
  Pack_Fault_ID: {
    address: 0x9084,
    name: 'Pack_Fault_ID',
    dataType: DataType.U16,
    description: `Real-time faulty battery pack number.
    Bit0-7: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
    Bit8-11: The serial number of the battery pack to be queried, the valid range is 0~15, and 0 means the first battery pack
    Bit12-15: The serial number of the queried fault, the valid range is 0~5, 0 means the most recent fault`,
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Fault ID',
    },
  },
  Pack_Fault_Timestamp: {
    address: 0x9085,
    name: 'Pack_Fault_Timestamp',
    dataType: DataType.U32,
    description: 'Fault data timestamp',
    access: ReadWrite.Read,
    quantity: 2,
    custom: {
      translate: 'Fault Timestamp',
    },
  },
  Pack_Fault_Timestamp_Converted: {
    address: 0x9085,
    name: 'Pack_Fault_Timestamp',
    dataType: DataType.U32,
    description: 'Fault data timestamp (converted)',
    access: ReadWrite.Read,
    quantity: 2,
    custom: {
      translate: 'Fault Date/Time',
    },
  },
  Pack_Fault_SN: {
    address: 0x9087,
    name: 'Pack_Fault_SN',
    dataType: DataType.ASCII,
    description: 'Device serial number',
    access: ReadWrite.Read,
    quantity: 10,
    custom: {
      translate: 'Serial Number',
    },
  },
  Pack_Fault_Cell_Max_Voltage: {
    address: 0x90A9,
    name: 'Pack_Fault_Cell_Max_Voltage',
    dataType: DataType.U16,
    description: 'The highest voltage of the battery',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.001,
    custom: {
      translate: 'Cell Max Voltage',
    },
  },
  Pack_Fault_Cell_Min_Voltage: {
    address: 0x90AA,
    name: 'Pack_Fault_Cell_Min_Voltage',
    dataType: DataType.U16,
    description: 'Minimum battery voltage',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.001,
    custom: {
      translate: 'Cell Min Voltage',
    },
  },
  Pack_Fault_Temperature1_Pack: {
    address: 0x90AB,
    name: 'Pack_Fault_Temperature1_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 1',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 1',
    },
  },
  Pack_Fault_Temperature2_Pack: {
    address: 0x90AC,
    name: 'Pack_Fault_Temperature2_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 2',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 2',
    },
  },
  Pack_Fault_Temperature3_Pack: {
    address: 0x90AD,
    name: 'Pack_Fault_Temperature3_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 3',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 3',
    },
  },
  Pack_Fault_Temperature4_Pack: {
    address: 0x90AE,
    name: 'Pack_Fault_Temperature4_Pack',
    dataType: DataType.I16,
    description: 'Battery pack temperature 4',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Temperature 4',
    },
  },
  Pack_Fault_Temperature_MOS: {
    address: 0x90AF,
    name: 'Pack_Fault_Temperature_MOS',
    dataType: DataType.I16,
    description: 'MOS tube temperature',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'MOS Temperature',
    },
  },
  Pack_Fault_Temperature_Env: {
    address: 0x90B0,
    name: 'Pack_Fault_Temperature_Env',
    dataType: DataType.I16,
    description: 'Ambient temperature in the battery pack',
    access: ReadWrite.Read,
    unit: '℃',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Ambient Temperature',
    },
  },
  Pack_Fault_Current: {
    address: 0x90B1,
    name: 'Pack_Fault_Current',
    dataType: DataType.I16,
    description: 'Battery pack current',
    access: ReadWrite.Read,
    unit: 'A',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Current',
    },
  },
  Pack_Fault_Remaining_Capacity: {
    address: 0x90B2,
    name: 'Pack_Fault_Remaining_Capacity',
    dataType: DataType.U16,
    description: 'Remaining capacity',
    access: ReadWrite.Read,
    unit: 'Ah',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Remaining Capacity',
    },
  },
  Pack_Fault_FullCharge_Capacity: {
    address: 0x90B3,
    name: 'Pack_Fault_FullCharge_Capacity',
    dataType: DataType.U16,
    description: 'Full charge capacity',
    access: ReadWrite.Read,
    unit: 'Ah',
    quantity: 1,
    accuracy: 0.1,
    custom: {
      translate: 'Full Charge Capacity',
    },
  },
  Pack_Fault_Total_Voltage: {
    address: 0x90B4,
    name: 'Pack_Fault_Total_Voltage',
    dataType: DataType.U16,
    description: 'Total Voltage',
    access: ReadWrite.Read,
    unit: 'V',
    quantity: 1,
    accuracy: 0.01,
    custom: {
      translate: 'Total Voltage',
    },
  },
  Pack_Alarm_State: {
    address: 0x90B5,
    name: 'Pack_Alarm_State',
    dataType: DataType.U16,
    description: 'Alarm status',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Alarm State',
    },
  },
  Pack_Protect_State: {
    address: 0x90B6,
    name: 'Pack_Protect_State',
    dataType: DataType.U16,
    description: 'Protection status',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Protection State',
    },
  },
  Pack_Fault_State: {
    address: 0x90B7,
    name: 'Pack_Fault_State',
    dataType: DataType.U16,
    description: 'Fault state',
    access: ReadWrite.Read,
    quantity: 1,
    custom: {
      translate: 'Fault State',
    },
  },
};

const Pack_Fault_Cell_Voltage_Config_Start = V3BmsFaultConfig.Pack_Fault_Cell_Voltage.start;
for (let i = 1; i <= V3BmsFaultConfig.Pack_Fault_Cell_Voltage.length; i++) {
  const { pack_fault_cell_voltage } = createPackFaultCellVoltage(
    i,
    Pack_Fault_Cell_Voltage_Config_Start + i,
  );
  V3BmsFaults[`Pack_Fault_Cell_Voltage${i}`] = pack_fault_cell_voltage;
}

export const ComposeV3BmsFaults: Partial<InverterDataComposer> = {
  Pack_Alarm_State: (data: Partial<InverterData>) => {
    const value = data.Pack_Alarm_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    // Uses same bit definitions as Pack_RT_Alarm_State (0x9076)
    data.Pack_Alarm_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9076);
    return data;
  },
  Pack_Protect_State: (data: Partial<InverterData>) => {
    const value = data.Pack_Protect_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    // Uses same bit definitions as Pack_RT_Protect_State (0x9077)
    data.Pack_Protect_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9077);
    return data;
  },
  Pack_Fault_State: (data: Partial<InverterData>) => {
    const value = data.Pack_Fault_State;
    if (value === undefined || typeof value !== 'number') {
      return data;
    }
    // Uses same bit definitions as Pack_RT_Fault_State (0x9078)
    data.Pack_Fault_State = value === 0 ? 'OK' : getActiveErrorDescriptions(value, 0x9078);
    return data;
  },
  Pack_Fault_Timestamp_Converted: (data: Partial<InverterData>) => {
    const binary = data.Pack_Fault_Timestamp?.toString(2).padStart(32, '0').split('').reverse().join('');
    if (!binary) {
      data.Pack_Fault_Timestamp_Converted = null;
      return data;
    }

    const second = parseInt(binary.slice(0, 6).split('').reverse().join(''), 2);
    const min = parseInt(binary.slice(6, 12).split('').reverse().join(''), 2);
    const hour = parseInt(binary.slice(12, 17).split('').reverse().join(''), 2);
    const day = parseInt(binary.slice(17, 22).split('').reverse().join(''), 2);
    const month = parseInt(binary.slice(22, 26).split('').reverse().join(''), 2);
    // year start from 2000 to 2063
    const year = 2000 + parseInt(binary.slice(26, 32).split('').reverse().join(''), 2);

    data.Pack_Fault_Timestamp_Converted = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
    return data;
  },
  Pack_Fault_ID_Parsed: (data: Partial<InverterData>) => {
    const value = data.Pack_Fault_ID as number;
    if (value === undefined) {
      return data;
    }
    // Bit0-7: battery pack serial number (0~15)
    const packSerial = value & 0xFF;
    // Bit8-11: battery string serial number (0~15)
    const stringSerial = (value >> 8) & 0x0F;
    // Bit12-15: fault number (0~5, 0 = most recent)
    const faultNumber = (value >> 12) & 0x0F;

    data.Pack_Fault_ID_Parsed = {
      type: CustomInverterType.Object,
      value: {
        packSerial,
        stringSerial,
        faultNumber,
      },
    };
    return data;
  },
};
